/**
 * AISummaryCard — Anthropic Claude-powered narrative summary at the top of
 * the Reports page (Phase 5E).
 *
 * Renders three markdown sections (Top Patterns / Anomalies / Recommended
 * Follow-ups) generated server-side from the same filter set the rest of
 * the page is using. The server caches results for 15 minutes keyed on
 * the filter hash, so a normal "refresh page" doesn't re-bill — only the
 * explicit "Regenerate" button does.
 *
 * The component is mounted unconditionally; the API returns 503 when
 * ANTHROPIC_API_KEY isn't provisioned, and we render a configuration
 * hint instead of a noisy error.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SparklesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { reportsApi, type AiSummary, type ReportFilterInput } from '../../services/reportsApi';

interface Props {
  filters: ReportFilterInput;
}

// Render a small subset of markdown — `## Section`, `**bold**`, and
// list items starting with `- ` or `* `. We deliberately avoid a full
// markdown parser because the model's output shape is tightly
// constrained by the system prompt; this keeps the bundle lean.
function renderMarkdown(md: string): JSX.Element {
  const lines = md.split('\n');
  const blocks: JSX.Element[] = [];
  let bulletBuffer: string[] = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="space-y-2 mt-2">
        {bulletBuffer.map((b, idx) => (
          <li key={idx} className="text-sm text-gray-300 leading-relaxed pl-4 relative">
            <span className="absolute left-0 top-1 text-accent">•</span>
            {renderInline(b)}
          </li>
        ))}
      </ul>,
    );
    bulletBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('## ')) {
      flushBullets();
      blocks.push(
        <h3 key={`h-${blocks.length}`} className="text-sm font-semibold text-white mt-4 first:mt-0 flex items-center gap-2">
          <span className="h-1 w-1 rounded-full bg-accent" />
          {line.slice(3)}
        </h3>,
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      bulletBuffer.push(line.slice(2));
    } else if (line.length === 0) {
      flushBullets();
    } else {
      flushBullets();
      blocks.push(
        <p key={`p-${blocks.length}`} className="text-sm text-gray-300 mt-2">
          {renderInline(line)}
        </p>,
      );
    }
  }
  flushBullets();
  return <>{blocks}</>;
}

function renderInline(text: string): JSX.Element {
  // Convert **bold** to <strong>. Single-pass split so we don't double-render.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
        }
        return <span key={idx}>{part}</span>;
      })}
    </>
  );
}

export default function AISummaryCard({ filters }: Props) {
  const queryClient = useQueryClient();
  const [forceNonce, setForceNonce] = useState(0);

  // Stable key off the filters so the result rehydrates from cache when the
  // user scrolls back to the page. nonce is appended to bust on Regenerate.
  const queryKey = useMemo(
    () => ['reports', 'ai-summary', filters, forceNonce],
    [filters, forceNonce],
  );

  const summaryQuery = useQuery<AiSummary, Error>({
    queryKey,
    queryFn: () => reportsApi.getAiSummary(filters, forceNonce > 0),
    // Don't refetch on every focus — this costs Anthropic tokens.
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => reportsApi.getAiSummary(filters, true),
    onSuccess: (data) => {
      // Bump the nonce so future renders read the fresh value out of
      // useQuery's cache instead of re-firing the request.
      setForceNonce((n) => n + 1);
      queryClient.setQueryData(['reports', 'ai-summary', filters, forceNonce + 1], data);
    },
  });

  // Distinguish 503 (config missing) from other errors so the empty state
  // tells operators something actionable.
  const error = summaryQuery.error as
    | (Error & { response?: { status?: number; data?: { error?: { message?: string } } } })
    | null;
  const isUnconfigured = error?.response?.status === 503;

  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-accent" />
          <h2 className="text-sm font-semibold text-white">AI Summary</h2>
          {summaryQuery.data?.cached && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-navy-800 text-gray-400 border border-navy-700">
              cached
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => regenerateMutation.mutate()}
          disabled={summaryQuery.isLoading || regenerateMutation.isPending || isUnconfigured}
          className="text-xs flex items-center gap-1 px-2 py-1 rounded-md text-gray-400 hover:text-white hover:bg-navy-800 disabled:opacity-50"
          title="Bust the cache and ask Claude for a fresh summary"
        >
          <ArrowPathIcon
            className={`h-3.5 w-3.5 ${
              regenerateMutation.isPending ? 'animate-spin' : ''
            }`}
          />
          Regenerate
        </button>
      </div>

      {summaryQuery.isLoading && (
        <div className="space-y-2">
          <div className="skeleton h-4 w-3/4" />
          <div className="skeleton h-4 w-2/3" />
          <div className="skeleton h-4 w-5/6" />
        </div>
      )}

      {isUnconfigured && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-navy-800/50 border border-navy-700 text-sm text-gray-400">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
          <div>
            <div className="text-white font-medium mb-0.5">AI summary not configured</div>
            <div className="text-xs">
              Set the <code className="text-accent">ANTHROPIC_API_KEY</code>{' '}
              environment variable on the API service to enable this feature.
            </div>
          </div>
        </div>
      )}

      {error && !isUnconfigured && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-400">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            {error.response?.data?.error?.message ?? error.message ?? 'AI summary failed'}
          </span>
        </div>
      )}

      {summaryQuery.data && !error && (
        <div className="ai-summary-prose">
          {renderMarkdown(summaryQuery.data.markdown)}
          <p className="text-[10px] text-gray-500 mt-4">
            Generated by {summaryQuery.data.model} · {new Date(summaryQuery.data.generatedAt).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
